import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import logo from './media/JoltLogistics_FullColor.svg';
import emailjs from "@emailjs/browser";
import {EMAILJS_CONFIG } from './config'
import {
  Truck,
  Thermometer,
  Package,
  Leaf,
  Phone,
  Mail,
  MapPin,
  ArrowRight,
  Building2,
  ShieldCheck,
} from "lucide-react";

// --- Quick Design Notes ---
// • Tailwind is used for styling. Brand accents lean on yellow + gray.
// • One-page layout with anchored nav (About, Services, Shippers, Carriers, Industries, Careers, Contact).
// • Contact/Quote form stores submission locally (placeholder). Hook to Formspree/Netlify/Email later.
// • Replace placeholders (address, emails, MC/DOT) before publishing.

const sections = [
  { id: "about", label: "About" },
  { id: "services", label: "Services" },
  { id: "shippers", label: "Shippers" },
  { id: "carriers", label: "Carriers" },
  { id: "industries", label: "Industries" },
  { id: "careers", label: "Careers" },
  { id: "contact", label: "Contact" },
];

const brand = {
  primary: "yellow-400",
  primaryText: "text-yellow-400",
  primaryBg: "bg-yellow-400",
  primaryRing: "ring-yellow-400/40",
};

function Container({ children, className = "" }) {
  return (
    <div className={`mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 ${className}`}>
      {children}
    </div>
  );
}

function Nav() {
  return (
    <header className="sticky top-0 z-50 backdrop-blur supports-[backdrop-filter]:bg-white/60 bg-white/80 border-b border-gray-200">
      <Container className="flex items-center justify-between h-16">
        <a href="#home" className="flex items-center gap-2">
          <img src={logo} className="h-6"/>
        </a>
        <nav className="flex md:hidden items-center gap-6">
          {sections.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              className="text-gray-600 hover:text-gray-900 transition-colors"
            >
              {s.label}
            </a>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-2">
          <a
            href="#contact"
            className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-gray-900 bg-${brand.primary} hover:opacity-90 transition`}
          >
            Get a Quote <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </Container>
    </header>
  );
}

function Hero() {
  return (
    <section id="home" className="relative overflow-hidden">
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-gray-50 via-white to-white" />
      <Container className="py-20 sm:py-28">
        <div className="grid gap-10 lg:grid-cols-2 lg:gap-16 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-gray-900">
              Reliable Full Truckload Capacity—
              <span className={`${brand.primaryText}`}> Dry Van & Refrigerated</span>
            </h1>
            <p className="mt-5 text-lg text-gray-600 max-w-xl">
              Chicago-based 3PL delivering fast coverage, clear communication, and consistent performance. Small enough to know your lanes. Big enough to keep them covered.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#contact" className={`inline-flex items-center gap-2 rounded-xl px-5 py-3 font-semibold text-gray-900 bg-${brand.primary} hover:opacity-90`}>
                Get a Quote <ArrowRight className="h-5 w-5" />
              </a>
              <a href="#services" className="inline-flex items-center gap-2 rounded-xl px-5 py-3 font-semibold text-gray-900 border border-gray-300 hover:bg-gray-50">
                Explore Services
              </a>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-6 text-sm text-gray-600">
              <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4"/>Compliance-first carrier vetting</div>
              <div className="flex items-center gap-2"><Package className="h-4 w-4"/>PODs within hours of delivery</div>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}>
            <div className="relative">
              <div className="absolute -inset-4 -z-10 rounded-3xl blur-2xl bg-yellow-200/30"/>
              <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
                <ul className="grid grid-cols-1 gap-4">
                  <li className="flex items-center gap-3">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-gray-900 text-yellow-400"><Truck className="h-5 w-5"/></span>
                    <div>
                      <div className="font-semibold">Dry Van FTL</div>
                      <div className="text-sm text-gray-600">Daily/weekly lanes, projects, seasonal surges</div>
                    </div>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-gray-900 text-yellow-400"><Thermometer className="h-5 w-5"/></span>
                    <div>
                      <div className="font-semibold">Refrigerated FTL</div>
                      <div className="text-sm text-gray-600">Food & beverage, ingredients, strict temp compliance</div>
                    </div>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-gray-900 text-yellow-400"><Package className="h-5 w-5"/></span>
                    <div>
                      <div className="font-semibold">Value-Add Support</div>
                      <div className="text-sm text-gray-600">Appointment setting, live driver support, claims coordination</div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </Container>
    </section>
  );
}

function SectionHeader({ eyebrow, title, subtitle }) {
  return (
    <div className="text-center max-w-3xl mx-auto">
      {eyebrow && (
        <div className="mb-2 inline-flex items-center rounded-lg border border-yellow-300 bg-yellow-50 px-3 py-1 text-xs font-semibold text-gray-900">
          {eyebrow}
        </div>
      )}
      <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">{title}</h2>
      {subtitle && <p className="mt-3 text-gray-600">{subtitle}</p>}
    </div>
  );
}

function About() {
  return (
    <section id="about" className="py-20 sm:py-24">
      <Container>
        <SectionHeader
          eyebrow="About Jolt"
          title="Built for speed, accountability, and consistent coverage"
          subtitle="We’re a Chicago-based, non-asset 3PL specializing in dry van and refrigerated full truckload across North America."
        />
        <div className="mt-10 grid gap-8 md:grid-cols-2">
          <div className="text-gray-700 leading-relaxed">
            Jolt Logistics pairs experienced operators with a quality-vetted carrier network and practical visibility tools. Our promise is simple: clear communication, predictable service, and no surprises. As we grow, we’re investing in partnerships that reduce waste and support a more sustainable freight ecosystem.
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Stat label="On-time performance focus" value="> 98%"/>
            <Stat label="Carrier vetting checks" value="Safety, COI, performance"/>
            <Stat label="Primary equipment" value="Dry Van, Reefer FTL, and Flatbed"/>
            <Stat label="HQ" value="Chicago, IL"/>
          </div>
        </div>
      </Container>
    </section>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-2xl border border-gray-200 p-4">
      <div className="text-sm text-gray-600">{label}</div>
      <div className="mt-1 text-lg font-semibold text-gray-900">{value}</div>
    </div>
  );
}

function Services() {
  const cards = useMemo(
    () => [
      {
        icon: <Truck className="h-6 w-6" />, title: "Full Truckload (Dry Van)",
        points: [
          "Daily/weekly lanes, projects, and seasonal surges",
          "Retail, packaging, consumer goods, more",
          "Proactive updates, PODs within hours",
        ],
      },
      {
        icon: <Thermometer className="h-6 w-6" />, title: "Refrigerated FTL (Temp-Controlled)",
        points: [
          "Food & beverage, ingredients, temp compliance",
          "Live driver support and appointment setting",
          "Exception management and claims coordination",
        ],
      },
      {
        icon: <Package className="h-6 w-6" />, title: "Dedicated & RFP Support",
        points: [
          "Committed capacity for stability",
          "Clear SLAs and KPI cadence",
          "Mini-bids for seasonal surges",
        ],
      },
    ],
    []
  );

  return (
    <section id="services" className="py-20 sm:py-24 bg-gray-50">
      <Container>
        <SectionHeader
          eyebrow="Services"
          title="Coverage without chaos"
          subtitle="Right-sized tech, quality carrier network, and operators who own outcomes from tender to delivery."
        />
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {cards.map((c, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm"
            >
              <div className="flex items-center justify-between">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gray-900 text-yellow-400">
                  {c.icon}
                </div>
              </div>
              <h3 className="mt-4 text-lg font-semibold text-gray-900">{c.title}</h3>
              <ul className="mt-3 space-y-2 text-gray-700">
                {c.points.map((p, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="mt-1 h-2 w-2 rounded-full bg-yellow-400" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </Container>
    </section>
  );
}

function TwoColList({ title, bullets }) {
  return (
    <div>
      <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
      <ul className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 text-gray-700">
        {bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="mt-1 h-2 w-2 rounded-full bg-yellow-400" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Shippers() {
  return (
    <section id="shippers" className="py-20 sm:py-24">
      <Container>
        <SectionHeader
          eyebrow="For Shippers"
          title="Fast coverage, proactive updates, rapid escalation"
          subtitle="Need a load covered today—or a plan for peak season? We can stand up new lanes with KPI targets and weekly cadence."
        />
        <div className="mt-10 grid gap-10 md:grid-cols-2">
          <TwoColList
            title="What you get"
            bullets={[
              "Clear pickup windows and live support",
              "Proactive status milestones + PODs",
              "Mini-bids and RFP support",
              "Exception management and claims coordination",
            ]}
          />
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <h3 className="text-xl font-semibold text-gray-900">Popular requests</h3>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {["Dry van weekly lane", "Reefer dedicated lane", "Holiday surge plan", "Expedited through partners"].map((x) => (
                <div key={x} className="rounded-xl border border-gray-200 p-4 text-gray-700">{x}</div>
              ))}
            </div>
            <a href="#contact" className="mt-6 inline-flex items-center gap-2 rounded-xl px-4 py-2 font-semibold text-gray-900 bg-yellow-400">
              Request a Quote <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </Container>
    </section>
  );
}

function Carriers() {
  return (
    <section id="carriers" className="py-20 sm:py-24 bg-gray-50">
      <Container>
        <SectionHeader
          eyebrow="For Carriers"
          title="Capacity is a partnership"
          subtitle="Consistent freight, straightforward onboarding, and fast issue resolution."
        />
        <div className="mt-10 grid gap-10 md:grid-cols-2">
          <TwoColList
            title="What we offer"
            bullets={[
              "Reputable lanes and reliable tenders",
              "Clear rate-cons and quick communication",
              "Standard & quick-pay options (ask for details)",
              "Compliance-first onboarding (COI, W-9, safety)",
            ]}
          />
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <h3 className="text-xl font-semibold text-gray-900">Become a Carrier Partner</h3>
            <p className="mt-2 text-gray-700">We’ll publish a portal link soon. For now, send your packet and lane preferences.</p>
            <div className="mt-4 flex flex-col gap-2 text-gray-700">
              <div className="flex items-center gap-2"><Mail className="h-4 w-4"/> masonhardman@jolt-logistics.com</div>
              <div className="flex items-center gap-2"><Phone className="h-4 w-4"/> (419) 788-3705</div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}

function Industries() {
  const items = [
    { icon: <Thermometer className="h-5 w-5"/>, name: "Food & Beverage", desc: "Reefer FTL, tight temp control, retail delivery" },
    { icon: <Package className="h-5 w-5"/>, name: "Packaging & Paper", desc: "High-volume dry van, steady cadence" },
    { icon: <Building2 className="h-5 w-5"/>, name: "Building Materials", desc: "Seasonal flexibility and surge plans" },
    { icon: <Truck className="h-5 w-5"/>, name: "Consumer Goods", desc: "Retail-ready shipments and projects" },
    { icon: <ShieldCheck className="h-5 w-5"/>, name: "Automotive & Parts", desc: "Time-sensitive replenishment" },
    { icon: <Leaf className="h-5 w-5"/>, name: "Chemicals", desc: "SDS-aware handling via vetted carriers" },
  ];

  return (
    <section id="industries" className="py-20 sm:py-24">
      <Container>
        <SectionHeader
          eyebrow="Industries"
          title="Where we’re a strong fit"
          subtitle="Focus on predictable FTL needs where consistency and communication matter most."
        />
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((it) => (
            <div key={it.name} className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gray-900 text-yellow-400">{it.icon}</div>
              <div className="mt-3 text-lg font-semibold text-gray-900">{it.name}</div>
              <div className="mt-1 text-gray-700">{it.desc}</div>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}

function Careers() {
  return (
    <section id="careers" className="py-20 sm:py-24 bg-gray-50">
      <Container>
        <SectionHeader
          eyebrow="Careers"
          title="Join the team"
          subtitle="We’re building a team of operators who own outcomes and love solving shipper + driver problems."
        />
        <div className="mt-10 grid gap-8 md:grid-cols-2">
          <div className="text-gray-700">
            <p>
              Early roles: <strong>Account Executive (Shippers)</strong> and future <strong>Carrier Sales</strong>.
              If you thrive on accountability and service, we’d like to talk.
            </p>
            <ul className="mt-4 space-y-2">
              {[
                "Ownership mentality and clear communication",
                "Bias for action and customer empathy",
                "Desire to improve processes and outcomes",
              ].map((x) => (
                <li key={x} className="flex items-start gap-2 text-gray-700">
                  <span className="mt-2 h-2 w-2 rounded-full bg-yellow-400" /> {x}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <h3 className="text-xl font-semibold text-gray-900">How to apply</h3>
            <p className="mt-2 text-gray-700">
              Send your resume and a note about why Jolt to
              <span className="font-semibold"> masonhardman@jolt-logistics.com</span>.
            </p>
          </div>
        </div>
      </Container>
    </section>
  );
}

  const SERVICE_ID = EMAILJS_CONFIG.SERVICE_ID;
  const TEMPLATE_ID = EMAILJS_CONFIG.TEMPLATE_ID;
  const PUBLIC_KEY = EMAILJS_CONFIG.PUBLIC_KEY;

function Contact() {
  const [submitted, setSubmitted] = useState(false);

  function onSubmit(e) {
    e.preventDefault();
    emailjs.sendForm(
      SERVICE_ID,
      TEMPLATE_ID,
      e.target,
      PUBLIC_KEY
    ).then(
        () => {
          setSubmitted(true);
          e.target.reset(); // optional — clears form
        },
        (err) => {
          console.error("EmailJS error:", err);
        }
      );
  }

  return (
    <section id="contact" className="py-20 sm:py-24">
      <Container>
        <SectionHeader
          eyebrow="Contact"
          title="Request a Quote"
          subtitle="Tell us about your shipment or question and we’ll get back quickly."
        />

        <div className="mt-10 grid gap-8 md:grid-cols-2">
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            {!submitted ? (
              <form onSubmit={onSubmit} className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="Name" name="name" required />
                  <Field label="Company" name="company" />
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="Email" name="email" type="email" required />
                  <Field label="Phone" name="phone" />
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="Origin" name="origin" required />
                  <Field label="Destination" name="destination" required />
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <Field label="Ready Date" name="ready_date" type="date" />
                  <Select label="Equipment" name="equipment" options={["Dry Van", "Refridgerated", "Flatbed"]} required />
                  <Field label="Weight (lbs)" name="weight" type="number" />
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <Field label="# Pallets" name="pallets" type="number" />
                  <Select label="Reason" name="reason" options={["Quote", "General Question", "Careers"]} />
                  <Field label="Reference #" name="reference" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-900">Special notes</label>
                  <textarea name="notes" className="mt-1 block w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-300" rows={4} placeholder="Dock hours, temp requirements, delivery appt, etc." />
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <input id="consent" name="consent" type="checkbox" className="h-4 w-4 rounded border-gray-300" required />
                  <label htmlFor="consent">You agree to be contacted about your request.</label>
                </div>
                <button type="submit" className="inline-flex items-center gap-2 rounded-xl px-5 py-3 font-semibold text-gray-900 bg-yellow-400 hover:opacity-90">
                  Submit Request <ArrowRight className="h-5 w-5" />
                </button>
                <p className="text-xs text-gray-500">We’ll never share your info.</p>
              </form>
            ) : (
              <div className="text-center py-10">
                <h3 className="text-2xl font-semibold text-gray-900">Thanks! We got your request.</h3>
                <p className="mt-2 text-gray-700">We’ll reach out shortly. If it’s urgent, call us.</p>
                <a href="tel:+14197883705" className="mt-4 inline-flex items-center gap-2 rounded-xl px-4 py-2 font-semibold text-gray-900 border border-gray-300">
                  <Phone className="h-4 w-4"/> Call Jolt
                </a>
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <h3 className="text-xl font-semibold text-gray-900">Contact details</h3>
            <div className="mt-4 space-y-3 text-gray-700">
              <div className="flex items-center gap-2"><Mail className="h-4 w-4"/> masonhardman@jolt-logistics.com</div>
              <div className="flex items-center gap-2"><Phone className="h-4 w-4"/> (419) 788-3705</div>
              <div className="flex items-center gap-2"><MapPin className="h-4 w-4"/> 1629 N Clybourn Ave, Unit B/G, Chicago, IL 60614</div>
            </div>
            
            
          </div>
        </div>
      </Container>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-gray-200 bg-white">
      <Container className="py-10">
        <div className="grid gap-8 md:grid-cols-2">
          <div>
            <img src={logo} className="h-10"/>
            <p className="mt-3 text-sm text-gray-600 max-w-sm">
              Non-asset 3PL specializing in Dry Van, Reefer FTL and Flatbed. Chicago HQ.
            </p>
          </div>          <div className="text-sm text-gray-700">
            <div className="font-semibold text-gray-900">Links</div>
            <ul className="mt-2 space-y-1">
              <li><a className="hover:underline" href="#services">Services</a></li>
              <li><a className="hover:underline" href="#shippers">Shippers</a></li>
              <li><a className="hover:underline" href="#carriers">Carriers</a></li>
              <li><a className="hover:underline" href="#contact">Get a Quote</a></li>
            </ul>
          </div>
        </div>
        <div className="mt-8 flex items-center justify-between border-t border-gray-200 pt-6 text-xs text-gray-500">
          <div>© {new Date().getFullYear()} Jolt Logistics. All rights reserved.</div>
          <div>Terms • Privacy</div>
        </div>
      </Container>
    </footer>
  );
}

function Field({ label, name, type = "text", required }) {
  return (
    <div>
      <label className="text-sm font-medium text-gray-900" htmlFor={name}>{label}{required && <span className="text-red-500">*</span>}</label>
      <input
        id={name}
        name={name}
        type={type}
        required={required}
        className="mt-1 block w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-300"
      />
    </div>
  );
}

function Select({ label, name, options = [], required }) {
  return (
    <div>
      <label className="text-sm font-medium text-gray-900" htmlFor={name}>{label}{required && <span className="text-red-500">*</span>}</label>
      <select id={name} name={name} required={required} className="mt-1 block w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-300">
        <option value="">Select…</option>
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </div>
  );
}

export default function JoltWebsite() {
  // Smooth scroll behavior
  React.useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.style.scrollBehavior = "smooth";
    }
  }, []);

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <Nav />
      <Hero />
      <About />
      <Services />
      <Shippers />
      <Carriers />
      <Industries />
      <Careers />
      <Contact />
      <Footer />
    </div>
  );
}
